import ApplicationController from "./application_controller";
import { computePosition, autoUpdate, offset, shift } from "@floating-ui/dom";
import { openDocumentPanel, openImagePanel } from "./panel/index.js";
import { setAction, setLink } from "./utils.js";

export default class extends ApplicationController {
  static targets = [
    "menu",
    "openDocument",
    "openLink",
    "openBundle",
    "presentationView",
    "downloadDocument",
    "downloadBundle",
    "downloadUnredacted",
    "downloadOriginal",
    "transcript",
    "editDocument",
    "createEditableDocument",
    "editBundle",
    "editNote",
    "properties",
    "newVersion",
    "versionHistory",
    "copy",
    "rotateImage",
    "redact",
    "compose",
    "reply",
    "replyAll",
    "forward",
    "resend",
    "importEmail",
    "deleteDocuments",
    "recoverDocuments",
    "purgeDocuments",
  ];

  singleDocument(document, config) {
    if (!document) {
      return;
    }
    this.documents = [document];
    if (document.deleted) {
      this.recoverDocuments(config);
      return;
    }

    const links = document.actionUrls;
    if (links.open && links.openBundle) {
      console.warn("document has open and openBundle links", document);
    }
    // singleDocument: pass documentUrl as the open URL for media files.
    // actionUrls.open is null for media (backend does not generate a preview URL
    // for audio/video), so falling back to documentUrl which is the raw streaming
    // URL served without save=yes (browser plays it natively in a new tab).
    this.setOpenLink(
      links.open || links.openBundle || (this.isMedia(document) ? document.documentUrl : null),
      this.isLink(document),
      this.isBundle(document),
    );
    this.setPresentationViewLink(links.presentationView);
    this.setDownloadLink(
      links.download || links.downloadBundle || links.downloadUnredacted,
      this.isBundle(document),
      this.isRedacted(document),
    );
    this.setDownloadOriginalLink(links.downloadOriginalMessage);
    this.setTranscriptLink(links.audioTranscript);
    this.setEditLink(
      links.enableEditing || links.edit,
      this.isEditable(document),
      this.isBundle(document),
    );
    this.setEditNoteLink(links.editNoteUrl);
    this.setPropertiesLink(links.propertiesUrl);
    this.setNewVersionLink(links.newVersionUrl);
    this.setVersionHistoryLink(links.versionHistory);
    this.setCopyLink(config.copyDocumentsUrl, document.copyable);
    this.setRotateLink(links.rotate);
    this.setRedactLink(links.redact);
    this.setComposeLink(config.composeEmailUrl, document.attachable);
    this.setReplyLink(links.replyUrl);
    this.setReplyAllLink(links.replyAllUrl);
    this.setForwardLink(links.forwardUrl);
    this.setResendLink(links.resendUrl);
    this.setImportEmailLink(links.importEmail);
    this.setDeleteLink(config.deleteDocumentsUrl, { deleteDocuments: true });
    this.setPurgeLink(config.purgeDocumentsUrl, false);
  }

  multipleDocuments(documents, config) {
    if (!documents) {
      return;
    }
    this.documents = documents;
    if (documents.some((document) => document.deleted)) {
      this.recoverDocuments(config);
      return;
    }

    this.setOpenLink(
      documents.some((document) => this.canOpen(document)),
      false,
      false,
    );
    this.setPresentationViewLink(null);
    this.setDownloadLink(null, false, false);
    this.setDownloadOriginalLink(null);
    this.setTranscriptLink(null);
    this.setEditLink(null, false, false);
    this.setEditNoteLink(null);
    this.setPropertiesLink(null);
    this.setNewVersionLink(null);
    this.setVersionHistoryLink(null);
    this.setCopyLink(
      config.copyDocumentsUrl,
      documents.every((document) => document.copyable),
    );
    this.setRotateLink(null);
    this.setRedactLink(null);
    this.setComposeLink(
      config.composeEmailUrl,
      documents.every((document) => document.attachable),
    );
    this.setReplyLink(null);
    this.setReplyAllLink(null);
    this.setForwardLink(null);
    this.setResendLink(null);
    this.setImportEmailLink(null);
    this.setDeleteLink(config.deleteDocumentsUrl, { deleteDocuments: true });
    this.setPurgeLink(config.purgeDocumentsUrl, false);
  }

  recoverDocuments(config) {
    this.setOpenLink(null, false, false);
    this.setPresentationViewLink(null);
    this.setDownloadLink(null, false, false);
    this.setDownloadOriginalLink(null);
    this.setTranscriptLink(null);
    this.setEditLink(null, false, false);
    this.setEditNoteLink(null);
    this.setPropertiesLink(null);
    this.setNewVersionLink(null);
    this.setVersionHistoryLink(null);
    this.setCopyLink(null, false);
    this.setRotateLink(null);
    this.setRedactLink(null);
    this.setComposeLink(null, false);
    this.setReplyLink(null);
    this.setReplyAllLink(null);
    this.setForwardLink(null);
    this.setResendLink(null);
    this.setImportEmailLink(null);
    this.setDeleteLink(config.recoverDocumentsUrl, { recoverDocuments: true });
    this.setPurgeLink(config.purgeDocumentsUrl, true);
  }

  isLink(document) {
    return document.documentType === "L" && document.actionUrls.open;
  }

  isBundle(document) {
    return document.documentType === "B" && document.actionUrls.openBundle;
  }

  isRedacted(document) {
    return document.redactedVersionId != null;
  }

  isEditable(document) {
    return document.editableCorrespondenceItem;
  }

  showNearElement(reference, timeStamp) {
    this.showMenu(reference, timeStamp, {
      placement: "bottom-end",
      middleware: [
        shift({
          crossAxis: true,
          padding: 30,
        }),
      ],
    });
  }

  showNearEventPoint({ clientX, clientY }, timeStamp) {
    const virtualElement = {
      getBoundingClientRect() {
        return {
          width: 0,
          height: 0,
          x: clientX,
          y: clientY,
          top: clientY,
          left: clientX,
          right: clientX,
          bottom: clientY,
        };
      },
    };
    this.showMenu(virtualElement, timeStamp, {
      placement: "bottom-start",
      middleware: [
        offset({ mainAxis: -30, crossAxis: -30 }),
        shift({
          crossAxis: true,
          padding: 30,
        }),
      ],
    });
  }

  showMenu(reference, timeStamp, options) {
    this.cleanupPositionAutoUpdate();
    this.positionCleanup = autoUpdate(
      reference,
      this.menuTarget,
      () => {
        computePosition(reference, this.menuTarget, options).then(
          ({ x, y }) => {
            Object.assign(this.menuTarget.style, {
              left: `${x}px`,
              top: `${y}px`,
            });
            if (!this.element.classList.contains("show")) {
              this.element.classList.add("show");
              this.menuTarget.classList.add("show");
              this.menuTarget.show();
              this.showEventTimeStamp = timeStamp;
            }
          },
        );
      },
      {
        ancestorScroll: false,
      },
    );
  }

  hide(e) {
    if (
      !this.element.classList.contains("show") ||
      e.timeStamp <= this.showEventTimeStamp
    ) {
      return;
    }
    this.element.classList.remove("show");
    this.menuTarget.classList.remove("show");
    this.menuTarget.close();
  }

  closed() {
    this.cleanupPositionAutoUpdate();
  }

  cleanupPositionAutoUpdate() {
    if (this.positionCleanup != null) {
      this.positionCleanup();
      this.positionCleanup = null;
    }
  }

  setOpenLink(url, isLink, isBundle) {
    const openDocumentTarget = this.openDocumentTarget;
    const openLinkTarget = this.openLinkTarget;
    const openBundleTarget = this.openBundleTarget;
    this.showMenuItem(openDocumentTarget, !isLink && !isBundle);
    this.showMenuItem(openLinkTarget, isLink);
    this.showMenuItem(openBundleTarget, isBundle);
    if (isLink) {
      this.setLink(openLinkTarget, url);
    } else if (isBundle) {
      this.setLink(openBundleTarget, url);
    } else {
      openDocumentTarget.classList.toggle("disabled", !url);
      openDocumentTarget.tabIndex = url ? 1 : -1;
      openDocumentTarget.disabled = !url;
    }
  }

  openDocuments() {
    this.documents.forEach((document) => {
      this.openDocument(document);
    });
  }

  openDocument(document) {
    // Media files (audio/video) must be opened in a new browser tab for native
    // playback. This check must come before the document.image branch because
    // video files have image:true from the backend (documentType:"I"), which
    // would otherwise incorrectly route them to openImagePanel.
    if (this.isMedia(document)) {
      const url = document.documentUrl;
      if (url) {
        window.open(this.decodeUrl(url), '_blank');
      }
      return;
    }
    if (!this.canOpen(document)) {
      return;
    }
    const options = {
      title: document.documentName,
      url: document.actionUrls.open,
    };
    if (document.image) {
      openImagePanel(options);
    } else {
      openDocumentPanel(options);
    }
  }

  canOpen(document) {
    return (
      (document.actionUrls.open || (this.isMedia(document) && document.documentUrl)) &&
      !(this.isLink(document) || this.isBundle(document))
    );
  }

  setPresentationViewLink(url) {
    this.setLink(this.presentationViewTarget, url);
  }

  setDownloadLink(url, isBundle, isRedacted) {
    const downloadDocumentTarget = this.downloadDocumentTarget;
    const downloadBundleTarget = this.downloadBundleTarget;
    const downloadUnredactedTarget = this.downloadUnredactedTarget;
    this.showMenuItem(downloadDocumentTarget, !isBundle && !isRedacted);
    this.showMenuItem(downloadBundleTarget, isBundle);
    this.showMenuItem(downloadUnredactedTarget, isRedacted);
    if (isBundle) {
      this.setLink(downloadBundleTarget, url);
    } else if (isRedacted) {
      this.setLink(downloadUnredactedTarget, url);
    } else {
      this.setLink(downloadDocumentTarget, url);
    }
  }

  setDownloadOriginalLink(url) {
    this.setLink(this.downloadOriginalTarget, url);
  }

  setTranscriptLink(url) {
    this.setLink(this.transcriptTarget, url);
  }

  setEditLink(url, isEditableDocument, isBundle) {
    const editDocumentTarget = this.editDocumentTarget;
    const createEditableDocumentTarget = this.createEditableDocumentTarget;
    const editBundleTarget = this.editBundleTarget;
    this.showMenuItem(editDocumentTarget, !url || isEditableDocument);
    this.showMenuItem(
      createEditableDocumentTarget,
      url && !(isEditableDocument || isBundle),
    );
    this.showMenuItem(editBundleTarget, isBundle);
    if (isBundle) {
      this.setLink(editBundleTarget, url);
    } else {
      this.setLink(editDocumentTarget, url);
      this.setLink(createEditableDocumentTarget, url);
    }
  }

  setEditNoteLink(url) {
    this.setLink(this.editNoteTarget, url);
  }

  setPropertiesLink(url) {
    this.setLink(this.propertiesTarget, url);
  }

  setNewVersionLink(url) {
    this.showMenuItem(
      this.newVersionTarget,
      toggles.files.showNewVersionMenuItem,
    );
    this.setLink(this.newVersionTarget, url);
  }

  setVersionHistoryLink(url) {
    this.setLink(this.versionHistoryTarget, url);
  }

  setCopyLink(url, copyable) {
    if (copyable) {
      const labels =
        this.documents.length == 1
          ? {
              documentName: this.documents[0].documentName,
            }
          : {
              documentName: `${this.documents.length} documents`,
            };
      this.enableForm(this.copyTarget, url, {
        documentParamName: "documentIds",
        labels,
      });
    } else {
      this.disableForm(this.copyTarget, { hide: false });
    }
  }

  setRotateLink(url) {
    this.setLink(this.rotateImageTarget, url);
  }

  setRedactLink(url) {
    this.setLink(this.redactTarget, url);
  }

  setComposeLink(url, attachable) {
    if (attachable) {
      this.enableForm(this.composeTarget, url, {
        documentParamName: "documentIds",
      });
    } else {
      this.disableForm(this.composeTarget, { hide: false });
    }
  }

  setReplyLink(url) {
    this.setLink(this.replyTarget, url);
  }

  setReplyAllLink(url) {
    this.setLink(this.replyAllTarget, url);
  }

  setForwardLink(url) {
    this.setLink(this.forwardTarget, url);
  }

  setResendLink(url) {
    this.setLink(this.resendTarget, url);
  }

  setPurgeLink(url, purgable) {
    if (purgable) {
      this.enableForm(this.purgeDocumentsTarget, url, {
        documentParamName: "actionRef",
      });
    } else {
      this.disableForm(this.purgeDocumentsTarget, { hide: false });
    }
  }

  setImportEmailLink(url) {
    this.setLink(this.importEmailTarget, url);
  }

  setDeleteLink(url, { deleteDocuments, recoverDocuments }) {
    const enabledFormTarget = deleteDocuments
      ? this.deleteDocumentsTarget
      : this.recoverDocumentsTarget;
    const disabledFormTarget = recoverDocuments
      ? this.deleteDocumentsTarget
      : this.recoverDocumentsTarget;
    const labels =
      this.documents.length == 1
        ? {
            actionLabel: deleteDocuments
              ? `Delete document - ${this.documents[0].documentName}`
              : `Recover deleted document - ${this.documents[0].documentName}`,
          }
        : {
            actionLabel: deleteDocuments
              ? `Delete ${this.documents.length} documents`
              : `Recover ${this.documents.length} deleted documents`,
          };
    this.enableForm(enabledFormTarget, url, {
      documentParamName: "actionRef",
      labels,
    });
    this.disableForm(disabledFormTarget, { hide: true });
  }

  enableForm(element, url, { documentParamName, labels }) {
    this.removeFormParams(element);
    this.setAction(element, url);
    element.classList.remove("d-none");
    element.querySelector(".dropdown-item").disabled = false;
    element.querySelector(".dropdown-item").classList.remove("disabled");
    this.addFormParam(
      element,
      documentParamName,
      this.documents.map((document) => document.globalId).join(","),
    );
    if (labels) {
      for (const name in labels) {
        this.addFormParam(element, name, labels[name]);
      }
    }
  }

  disableForm(element, { hide }) {
    this.removeFormParams(element);
    this.setAction(element, null);
    if (hide) {
      element.classList.add("d-none");
    }
    element.querySelector(".dropdown-item").classList.add("disabled");
    element.querySelector(".dropdown-item").disabled = true;
  }

  showMenuItem(element, state) {
    element.classList.toggle("d-none", !state);
    element.classList.toggle("disabled", !state);
  }

  setLink(element, url) {
    setLink(element, this.decodeUrl(url));
  }

  setAction(element, url) {
    setAction(element, this.decodeUrl(url));
  }

  removeFormParams(element) {
    element
      .querySelectorAll("input[type='hidden']")
      .forEach((child) => element.removeChild(child));
  }

  addFormParam(element, name, value) {
    element.insertAdjacentHTML(
      "beforeend",
      `<input type="hidden" name="${name}" value="${value}">`,
    );
  }

  // This isn't a general decoder of HTML entities; it just reverses the
  // encoding of `HTMLTools.encodeUrl`
  decodeUrl(url) {
    return url ? url.replaceAll("&amp;", "&") : url;
  }

  // Returns true for audio/video files based on the structured mediaType object
  // provided by the backend. Used to route media files to browser-native playback
  // instead of the document/image preview panel, which cannot render media.
  isMedia(document) {
    const type = document.mediaType?.type;
    return type === 'video' || type === 'audio';
  }
}
